var _ = require('underscore');
var $ = require('jquery');
var TorsoFormView = require('backbone-torso/modules/FormView');
var financialFeatureConfigModel = require('./../financial/financialFeatureConfigModel');
var billingConfigModel = require('./../financial/billingConfigModel');
var paymentDetailsUtils = require('./paymentDetailsUtils');
var DemographicsDataBehavior = require('./../demographics/DemographicsDataBehavior');
var paymentDetailsCreditCardTemplate = require('./paymentDetailsCreditCard.hbs');
var creditCardEncryptionService = require('./creditCardEncryptionService');

var Formatter = require('formatter-vecna');
var ENCRYPTED_CREDIT_CARD_HIDDEN_INPUT_ID = 'card-data_encrypted-card-info';

/**
 * The PaymentDetailsCreditCardView is a form view that handles credit card related actions.
 *
 * @class PaymentDetailsCreditCardView
 * @extends Torso.View
 */
module.exports = TorsoFormView.extend({
  className: 'portal-container portal-container--expanded-fields',
  moduleName: 'creditCardView',
  template: paymentDetailsCreditCardTemplate,

  /**
   * View State flag to determine whether the user is storing this payment method
   * @property viewState.isStoredPayment
   * @type boolean
   */

  /**
   * The array of possible credit card expiration years, used in populating the expiration year
   * dropdown menu.
   *
   * @property _expirationYears
   * @type Array of integers
   */
  _expirationYears: [],

  behaviors: {
    demographics: {
      behavior: DemographicsDataBehavior,
      returnSingleResult: true,
      renderOnFetch: true,
      id: { property: '_patientVecnaId' }
    }
  },

  prepareFields: {
    expirationYears: '_expirationYears',
    encryptedCreditCardHiddenInputId: ENCRYPTED_CREDIT_CARD_HIDDEN_INPUT_ID,
    usingMockProcessor: function() {
      return creditCardEncryptionService.isUsingMockProcessor();
    },
    usingGipProcessor: function() {
      return creditCardEncryptionService.isUsingGipProcessor();
    },
    usingTempusProcessor: function() {
      return creditCardEncryptionService.isUsingTempusProcessor();
    },
    selectSavePaymentMethod: function() {
      var isEnteringNewStoredPaymentOutsideOfPayment = this.model.get('isStoredPayment');
      return this.model.get('storePayment.save') || isEnteringNewStoredPaymentOutsideOfPayment;
    },
    canSelectSavePaymentMethod: function() {
      var isEnteringNewStoredPaymentOutsideOfPayment = this.model.get('isStoredPayment');
      var isStoredPaymentMethodEnabled = financialFeatureConfigModel.isStoredPaymentMethodsEnabled();
      return isStoredPaymentMethodEnabled && !isEnteringNewStoredPaymentOutsideOfPayment;
    },
    storedPmtEnabledOnCurrentApp: function() {
      return financialFeatureConfigModel.storedPmtEnabledOnCurrentApp();
    }
  },

  fields: {
    'cardData.cardNumber' : {
      /**
       * Store card number without spaces.
       * @param value the value being used to try and set on the model
       * @returns the formatted value appropriate to be set on the model
       * @method fields['cardData.cardNumber'].modelFormat
       */
      modelFormat: function(value) {
        if (value === null) {
          return '';
        } else {
          return value.replace(/\s/g, '');
        }
      },
      /**
       * Just return the value and let the front end format it.
       * @param value the value being used to try and set on the view
       * @returns the formatted value appropriate to be set on the view
       * @method fields['cardData.cardNumber'].viewFormat
       */
      viewFormat: function(value) {
        return value;
      }
    }
  },

  /**
   * Configures feedback bindings.
   * @property feedback {Array of When/Then Feedbacks}
   */
  feedback: [{
    //******** Card Name *********
    /**
     * On keyup or blur on the card name, validate the card name and add the error
     * message if needed
     * @property feedback[0] (Card Name Error Message) {When/Then Feedback}
     */
    when: {
      '@accountOwnerName' : ['keyup', 'blur'],
      'on' : ['submit']
    },
    then: function() {
      if (this._shouldValidate()) {
        var errors = this.model.preValidate('accountOwnerName');
        var errorText = this._thenFormatFeedback(errors, {
          'required.failed': 'card.name.required',
          'error.digits.only' : 'card.name.digitsOnly',
          'error.length' : 'card.name.length.incorrect'
        });
        var errorClass = this._thenAddClassIfInvalid('accountOwnerName', 'brec-feedback--error');
        var errorTestAttribute = { attr: ['data-test-name-validation', 'false'] };
        if (errors) {
          errorTestAttribute = { attr: ['data-test-name-validation', errors.key] };
        }
        return _.extend(errorText, errorClass, errorTestAttribute);
      }
    },
    to: 'accountOwnerNameError'
  },
  /**
   * On keyup or blur on the card name, validate the card name and add the error
   * class to needed elements
   * @property feedback[1] (Card name label) {When/Then Feedback}
   */
  {
    when: {
      '@accountOwnerName' : ['keyup', 'blur'],
      'on' : ['submit']
    },
    then: function() {
      if (this._shouldValidate()) {
        return this._thenAddClassIfInvalid('accountOwnerName', 'brec-form__input--error');
      }
    },
    to: 'accountOwnerName'
  },
  //******** Card Number *********
  /**
   * On keyup or blur on the card number, validate the card number and add the error
   * message if needed
   * @property feedback[2] (Card Number Error Message) {When/Then Feedback}
   */
  {
    when: {
      '@cardData.cardNumber' : ['keyup', 'blur'],
      'on' : ['submit']
    },
    then: function() {
      var newCardType = this.model.getCardTypeByNumber();
      this.model.set('cardData.type', newCardType.name);
      if (this._shouldValidate()) {
        var errorText = this._thenFormatFeedback(this.model.preValidate('cardData.cardNumber'), {
          'required.failed': 'field.enter',
          'error.number.wrong': 'card.number.incorrect',
          'error.length': {
            key: 'card.number.length.incorrect',
            params: newCardType.lengths.join(' ' + this.format('common.or') + ' ')
          },
          'error.unknown': 'card.type.unknown'
        }, this.format('card.number'));
        var errorClass = this._thenAddClassIfInvalid('cardData.cardNumber', 'brec-feedback--error');
        return _.extend(errorText, errorClass);
      }
    },
    to: 'cardNumberError'
  },
  /**
   * On keyup or blur on the card number, validate the card number and add the error
   * class to needed elements
   * @property feedback[3] (Card Number Label) {When/Then Feedback}
   */
  {
    when: {
      '@cardData.cardNumber' : ['keyup', 'blur'],
      'on' : ['submit']
    },
    then: function() {
      if (this._shouldValidate()) {
        return this._thenAddClassIfInvalid('cardData.cardNumber', 'brec-form__input--error');
      }
    },
    to: 'cardData.cardNumber'
  },
  /**
   * On card number change, update the hidden field that reflects the card number w/o spaces.
   * @property feedback[4] (Hidden card number) {When/Then Feedback}
   */
  {
    when: {
      '@cardData.cardNumber' : ['keyup', 'blur'],
      'on' : ['submit']
    },
    then: function() {
      return {
        val: this.model.get('cardData.cardNumber')
      };
    },
    to: 'cardData.cardNumber.numericOnly'
  },
  /**
   * Dims or undims the visa icon if the card number matches the pattern for that card type
   * @property feedback[4] (Visa Icon) {When/Then Feedback}
   */
  {
    when: {
      'on': ['cardTypeChange']
    },
    then: function() {
      return this.thenHandleCardTypeDimming(this.model.CARDS.visa);
    },
    to: 'ccVisaImage'
  },
  /**
   * Dims or undims the mastercard icon if the card number matches the pattern for that card type
   * @property feedback[5] (Mastercard Icon) {When/Then Feedback}
   */
  {
    when: {
      'on': ['cardTypeChange']
    },
    then: function() {
      return this.thenHandleCardTypeDimming(this.model.CARDS.mastercard);
    },
    to: 'ccMCImage'
  },
  /**
   * Dims or undims the amex icon if the card number matches the pattern for that card type
   * @property feedback[6] (Amex Icon) {When/Then Feedback}
   */
  {
    when: {
      'on': ['cardTypeChange']
    },
    then: function() {
      return this.thenHandleCardTypeDimming(this.model.CARDS.amex);
    },
    to: 'ccAmexImage'
  },
  /**
   * Dims or undims the discover icon if the card number matches the pattern for that card type
   * @property feedback[7] (Discover Icon) {When/Then Feedback}
   */
  {
    when: {
      'on': ['cardTypeChange']
    },
    then: function() {
      return this.thenHandleCardTypeDimming(this.model.CARDS.discover);
    },
    to: 'ccDiscoverImage'
  },
  //******** Expiration Date *********
  /**
   * On change of the expiration date, validate the expiration month and add the error
   * class to month field
   * @property feedback[8] (Expiration Month Error Class) {When/Then Feedback}
   */
  {
    when: {
      '@cardData.expirationMonth' : ['change', 'click'],
      '@cardData.expirationYear' : ['change', 'click'],
      'on' : ['submit']
    },
    then: function() {
      if (this._shouldValidate()) {
        return this._thenAddClassIfInvalid(['cardData.expirationMonth', 'expirationDateInPast'], 'brec-form__input--error');
      }
    },
    to: 'cardData.expirationMonth'
  },
  /**
   * On change of the expiration date, validate the expiration year and add the error
   * class to year field
   * @property feedback[9] (Expiration Year Error Class) {When/Then Feedback}
   */
  {
    when: {
      '@cardData.expirationMonth' : ['change', 'click'],
      '@cardData.expirationYear' : ['change', 'click'],
      'on' : ['submit']
    },
    then: function() {
      if (this._shouldValidate()) {
        return this._thenAddClassIfInvalid(['cardData.expirationYear', 'expirationDateInPast'], 'brec-form__input--error');
      }
    },
    to: 'cardData.expirationYear'
  },
  /**
   * On change of the expiration date, validate the expiration date and add an error message
   * if needed
   * @property feedback[11] (Expiration Date Error Message) {When/Then Feedback}
   */
  {
    when: {
      '@cardData.expirationMonth' : ['change', 'click'],
      '@cardData.expirationYear' : ['change', 'click'],
      'on' : ['submit']
    },
    then: function() {
      if (this._shouldValidate()) {
        var monthError = this.model.preValidate('cardData.expirationMonth');
        var yearError = this.model.preValidate('cardData.expirationYear');
        var futureError = this.model.preValidate('expirationDateInPast');

        if (monthError && yearError) {
          return {
            text: this.format('field.select', this.format('card.expiration.date')),
            addClass: 'brec-feedback--error'
          };
        } else if (monthError) {
          return {
            text: this.format('field.select', this.format('card.month')),
            addClass: 'brec-feedback--error'
          };
        } else if (yearError) {
          return {
            text: this.format('field.select', this.format('card.year')),
            addClass: 'brec-feedback--error'
          };
        } else if (futureError) {
          return {
            text: this.format('card.expiration.date.error.inPast'),
            addClass: 'brec-feedback--error'
          };
        } else {
          return {
            text: '',
            removeClass: 'brec-feedback--error'
          };
        }
      }
    },
    to: 'expirationDateError'
  },
  //******** Security Code *********
  /**
   * On keyup or blur of either the security code or card number,
   * validate the security code and add error class on needed elements
   * @property feedback[12] (Security Code Label) {When/Then Feedback}
   */
  {
    when: {
      '@cardData.securityCode' : ['keyup', 'blur'],
      'on' : ['submit']
    },
    then: function() {
      if (this._shouldValidate()) {
        return this._thenAddClassIfInvalid('cardData.securityCode', 'brec-form__input--error');
      }
    },
    to: 'cardData.securityCode'
  },
  /**
   * On a credit card type change, update the icon showing where the cvc numbers are
   * @property feedback[13] (CVC Icon) {When/Then Feedback}
   */
  {
    when: {
      'on': ['cardTypeChange']
    },
    then: function() {
      var cardType = this.model.get('cardData.type');
      if (cardType === this.model.CARDS.amex.name) {
        return {
          removeClass: 'portal-cc-icon__cvv',
          addClass: 'portal-cc-icon__amex-cvv'
        };
      } else {
        return {
          removeClass: 'portal-cc-icon__amex-cvv',
          addClass: 'portal-cc-icon__cvv'
        };
      }
    },
    to: 'cardCvcIcon'
  },
  /**
   * On keyup or blur of either the security code or card number,
   * validate the security code and add error message
   * @property feedback[14] (Security Code Error Message) {When/Then Feedback}
   */
  {
    when: {
      '@cardData.securityCode' : ['keyup', 'blur'],
      'on' : ['submit']
    },
    then: function() {
      if (this._shouldValidate()) {
        var fieldName = this.format('card.verification.code'),
            error = this.model.preValidate('cardData.securityCode');

        if (error.key) {
          if (error.key === 'required.failed') {
            return {
              text: this.format('field.missing.error', fieldName),
              addClass: 'brec-feedback--error'
            };
          } else if (error.key === 'error.numDigits') {
            if (error.params === 'Amex') {
              return {
                text: this.format('card.verification.code.incomplete.amex'),
                addClass: 'brec-feedback--error'
              };
            } else {
              return {
                text: this.format('card.verification.code.incomplete.default'),
                addClass: 'brec-feedback--error'
              };
            }
          }
        } else if (error === 'error.nonNumeric') {
          return {
            text: this.format('card.verification.code.error.nonNumeric'),
            addClass: 'brec-feedback--error'
          };
        } else {
          return {
            text: '',
            removeClass: 'brec-feedback--error'
          };
        }
      }
    },
    to: 'securityCodeError'
  },
  /**
   * On a card encryption, validate the encrypted card info and add error message if needed
   * @property feedback[15] (Card Encryption Error Message) {When/Then Feedback}
   */
  {
    when: {
      listenTo: [{
        object: 'model',
        events: 'change:cardData.encryptedCardInfo change:cardEncryptionErrors'
      }]
    },
    then: function() {
      var feedbackResult = this._thenFormatFeedback(this.model.preValidate('cardData.encryptedCardInfo'), {
        'required.failed': 'card.encryption.error',
        'encryption.failed': 'card.encryption.error',
        'script.failed': 'card.encryption.download.error'
      });
      if (feedbackResult.text) {
        feedbackResult.addClass = 'brec-feedback--error';
      } else {
        feedbackResult.removeClass = 'brec-feedback--error';
      }
      return feedbackResult;
    },
    to: 'cardData.encryptedCardInfo'
  },
  {
    //******** Stored Payment Method Nickname *********
    /**
     * On keyup or blur on the stored payment method nickname or submits, validate the nickname and add the error
     * message if needed
     * @property feedback[16] (Nickname Error Message) {When/Then Feedback}
     */

    when: {
      '@storePayment.nickname' : ['keyup', 'blur'],
      '@storePayment.save': ['change'],
      'on' : ['submit']
    },
    then: function() {
      if (this._shouldValidate()) {
        var nickname = $.trim(this.model.get('storePayment.nickname'));
        var errorText = this._thenFormatFeedback(this.model.preValidate('storePayment.nickname'), {
          'required.failed' : 'field.enter',
          'error.too.short' : 'card.nickname.length.too.short',
          'error.too.long' : 'card.nickname.length.too.long',
          'error.duplicate' : 'card.nickname.duplicate'
        }, [ this.format('storePayment.nickname.creditCard'), nickname ? nickname.length : 0 ]);
        var errorClass = this._thenAddClassIfInvalid('storePayment.nickname', 'brec-feedback--error');
        return _.extend(errorText, errorClass);
      }
    },
    to: 'nicknameError'
  },
  {
    when: {
      '@storePayment.nickname' : ['keyup', 'blur'],
      '@storePayment.save': ['change'],
      'on' : ['submit']
    },
    then: function() {
      if (this._shouldValidate()) {
        return this._thenAddClassIfInvalid('storePayment.nickname', 'brec-form__input--error');
      }
    },
    to: 'storePayment.nickname'
  }],

  /**
   * Initializes the form view responsible for handling credit card information.
   *
   * @method initialize
   *   @param options.patientVecnaId {String} the vecna id of the patient.
   * @override
   */
  initialize: function(options) {
    _.bindAll(this, '_defaultAccountOwnerName', '_encryptCreditCardDataSuccess', '_encryptCreditCardDataFailure',
                    '_validateEncryptedField', '_triggerPaymentDataReady');
    this._patientVecnaId = options.patientVecnaId;
    var currentYear, i;

    // Compute the list of the next 10 years from the current year for populating the expiration
    // date dropdown
    currentYear = (new Date()).getFullYear();
    this._expirationYears = [];
    for (i = currentYear; i <= currentYear + 10; i++) {
      this._expirationYears.push(i);
    }

    this.listenTo(this.model, 'change:cardData.type', function() {
      this.trigger('cardTypeChange');
    });

    this.listenTo(this.model, 'change:storePayment', this.render);

    this.on('submit', this._encryptedCreditCardData);

    this.listenTo(financialFeatureConfigModel, 'load-complete', this.render);
    financialFeatureConfigModel.loadOnce();

    this.listenTo(billingConfigModel, 'load-complete', this.render);
    billingConfigModel.loadOnce();

    this.listenTo(this.getBehavior('demographics'), 'fetched change:acceptedDemographics.contactInfo.name', this._defaultAccountOwnerName);
    this._defaultAccountOwnerName();
  },

  /**
   * Overrides parent method prerender to remove any plugins that were used
   * @method prerender
   */
  prerender: function() {
    if (this._$creditCardElement) {
      this._$creditCardElement.remove();
    }
  },

  /**
   * Overrides parent method postrender which instantiates any plugins
   * @method postrender
   */
  postrender: function() {
    var $creditCardElement = this.$el.find('[data-cc-number]');
    this._$creditCardElement = $creditCardElement;
    var cards = _.map(this.model.CARDS, function(card) {
      var entry = {};
      entry[paymentDetailsUtils.convertRegexToString(card.regex)] = card.pattern;
      return entry;
    }, this);
    var creditCardElement = $creditCardElement.get(0);
    this._ccFormatter = new Formatter(creditCardElement, {
      patterns: cards,
      persistent: false,
      inputFn: function() {
        $creditCardElement.trigger('input');
      }
    });
    this._ccFormatter.resetPattern();
  },

  /**
   * Call Tempus encryption and set the "encryptedCardInfo" field on the cardData with the output.
   * If the given cardData already has the populated encryptedCardInfo, does nothing and returns true
   * @method _encryptedCreditCardData
   * @return {jQuery.Deferred.Promise} that resolves if the card data is encrypted and rejects if the encryption fails.
   * @private
   */
  _encryptedCreditCardData: function() {
    // Skip if not attached since BuildXML requires the dom to exist to save the encrypted value to.
    if (!this.isAttached() || !this.isActive()) {
      this._triggerPaymentDataReady();
      return $.Deferred().resolve().promise();
    }

    var isEncryptionScriptAvailable = creditCardEncryptionService.isEncryptionScriptAvailable();

    if (!isEncryptionScriptAvailable) {
      this.model.set('cardData.encryptedCardInfo', 'script.failed');
    }

    var cardNumber = this.model.get('cardData.cardNumber');
    var month = this.model.get('cardData.expirationMonth');
    month = month && month.toString();
    var fullYear = this.model.get('cardData.expirationYear');
    fullYear = fullYear && fullYear.toString();
    var year = fullYear && fullYear.substring(2, 4);
    var expiration = month && year && month + year;
    var securityCode = this.model.get('cardData.securityCode');
    var cardType = this.model.get('cardData.type');
    var canEncrypt = cardNumber && expiration && securityCode && isEncryptionScriptAvailable;
    if (!canEncrypt) {
      return false;
    }

    var creditCardData = {
      cardNumber: cardNumber,
      cardType: cardType,
      expiration: expiration,
      securityCode: securityCode,
      encryptedCardInfoHiddenInputId: ENCRYPTED_CREDIT_CARD_HIDDEN_INPUT_ID,
      $encryptedCreditCardResultHiddenInput: this.$el.find('#' + ENCRYPTED_CREDIT_CARD_HIDDEN_INPUT_ID)
    };
    return creditCardEncryptionService.encryptCreditCard(creditCardData)
        .then(this._encryptCreditCardDataSuccess, this._encryptCreditCardDataFailure)
        .then(this._validateEncryptedField, this._validateEncryptedField)
        .then(this._triggerPaymentDataReady);
  },

  /**
   * Trigger that the payment data is ready on the payment details model.
   *
   * @method _triggerPaymentDataReady
   * @private
   */
  _triggerPaymentDataReady: function() {
    this.model.trigger('paymentDataReady');
  },

  /**
   * Callback that is executed once the encrypted card data is generated successfully.
   * @method _encryptCreditCardDataSuccess
   * @param encryptedCreditCard {String} the encrypted credit card data.
   * @private
   */
  _encryptCreditCardDataSuccess: function(encryptedCreditCard) {
    this.model.set('cardData.encryptedCardInfo', encryptedCreditCard);
  },

  /**
   * Callback that is executed when the card data encryption fails.
   * @method _encryptCreditCardDataFailure
   * @private
   */
  _encryptCreditCardDataFailure: function() {
    this.model.set('cardData.encryptedCardInfo', 'encryption.failed');
  },

  /**
   * Callback that is executed when the card encrypted data changes so it can validate the field.
   * @method _validateEncryptedField
   * @private
   */
  _validateEncryptedField: function() {
    this.model.set('cardEncryptionErrors', this.model.preValidate('cardData.encryptedCardInfo'));
  },

  /**
   * @return {Boolean} true if the feedback validations should occur
   * @private
   * @method _shouldValidate
   */
  _shouldValidate: function() {
    return this.isAttached();
  },

  /**
   * Either adds or removes the dim class if model's card type matches the card type passed in
   * @param cardType {Object} the card type specified by the form model's CARD field
   * @return {Object} an object that either specifies to remove or add the dim class
   * @method thenHandleCardTypeDimming
   */
  thenHandleCardTypeDimming: function(cardType) {
    if (this.model.getCardTypeByNumber().name === cardType.name) {
      return {
        removeClass: 'portal-cc-icon--dim',
        attr: ['data-test-visible', true]
      };
    } else {
      return {
        addClass: 'portal-cc-icon--dim',
        attr: ['data-test-visible', false]
      };
    }
  },

  /**
   * Default the account owner name to the current user if it has not already been set on the form model
   *
   * @method _defaultAccountOwnerName
   * @private
   */
  _defaultAccountOwnerName: function() {
    var name = this.getBehavior('demographics').data.get('acceptedDemographics.contactInfo.name');
    var accountOwnerName = name && name.givenName + ' ' + name.familyName;
    var shouldDefaultAccountOwnerName = accountOwnerName && !this.model.get('accountOwnerName');

    if (shouldDefaultAccountOwnerName) {
      this.model.set('accountOwnerName', accountOwnerName);
    }
  },

  /**
   * @method _activate
   * @override
   * @private
   */
  _activate: function() {
    this.model.clearAllPaymentMethodData();
  },

  /**
   * Clear feedback on deactivate so that when it is selected again it doesn't start with feedback shown.
   * @method _deactivate
   * @private
   */
  _deactivate: function() {
    this.feedbackCell.clear();
  }
});
